// Download de anexo pelo comitê — busca o ciphertext e decifra NO BROWSER.
//
// Pedido do operador (2026-08-13): "os arquivos devem ser acessíveis e possíveis de
// baixar" na aba Mensagens.
//
// ── O servidor continua cego, e é isso que a divisão aqui protege
//
// `GET /cases/:id/attachment/:hash` devolve BYTES CIFRADOS e o nonce num header. A DEK
// nunca sai do browser: ela é obtida da keypair do membro (`withCaseDek`), a mesma via
// pela qual o teor da denúncia é lido. O Worker não tem como decifrar o que serve —
// ADR-0002 intacto.
//
// AAD = `caseId`, espelhando `attachments-upload.ts` do intake (que cifra com
// `encryptBytes(plaintext, dekRaw, caseId)`). ⚠️ Se essa AAD divergir, a decifra falha
// com erro de tag e parece "arquivo corrompido" — o modo de falha mais confuso possível.
// As duas pontas são o par: mexer numa exige mexer na outra.
//
// ── Por que o nome do arquivo NÃO vem daqui
//
// Ele nunca foi persistido: o upload manda só bytes cifrados, contentType e tamanho, e
// o `AttachmentRef` guarda `{contentHash, nonce, sizeBucket}`. Isso é desenho de
// anonimato — nome de arquivo é metadado que identifica ("laudo-joao-silva.pdf").
//
// O denunciante ANUNCIA os nomes na mensagem (cifrada), e é de lá que o comitê os lê.
// Aqui o download usa um nome derivado do hash: honesto sobre o que se sabe, em vez de
// inventar um rótulo.

import { decryptBytes } from '@aions/crypto';
import { apiFetch } from './auth-client';

export type AnexoDownloadErro = 'indisponivel' | 'sem_acesso' | 'falha_decrypt' | 'rede';

export interface AnexoBaixado {
  readonly bytes: Uint8Array;
  /** Nome sugerido, derivado do hash — ver o cabeçalho sobre o nome real. */
  readonly nomeSugerido: string;
}

/**
 * Busca, decifra e devolve os bytes em claro. NÃO dispara o download — quem faz isso é
 * a tela, para que o erro possa ser mostrado no lugar certo em vez de virar um arquivo
 * corrompido na pasta de downloads da pessoa.
 */
export async function baixarAnexo(
  caseId: string,
  contentHash: string,
  dekRaw: Uint8Array,
): Promise<{ ok: true; valor: AnexoBaixado } | { ok: false; erro: AnexoDownloadErro }> {
  let resp: Response;
  try {
    resp = await apiFetch(`/api/comite/cases/${caseId}/attachment/${contentHash}`);
  } catch {
    return { ok: false, erro: 'rede' };
  }

  if (resp.status === 403 || resp.status === 412) return { ok: false, erro: 'sem_acesso' };
  if (!resp.ok) {
    // 404 cobre "anexo purgado pela retenção" (estado legítimo, LGP-04) e "hash não é
    // deste caso". A tela não precisa distinguir: nos dois casos não há arquivo.
    return { ok: false, erro: 'indisponivel' };
  }

  const nonceB64 = resp.headers.get('X-Attachment-Nonce');
  if (nonceB64 === null || nonceB64 === '') {
    // Sem nonce não há decifra possível. Erro EXPLÍCITO em vez de tentar e falhar na
    // tag: "falha ao decifrar" mandaria o membro procurar problema no arquivo.
    return { ok: false, erro: 'falha_decrypt' };
  }

  try {
    const ciphertext = new Uint8Array(await resp.arrayBuffer());
    const nonce = base64ParaBytes(nonceB64);
    const bytes = await decryptBytes(ciphertext, nonce, dekRaw, caseId);
    return {
      ok: true,
      valor: { bytes, nomeSugerido: `anexo-${contentHash.slice(0, 12)}` },
    };
  } catch {
    return { ok: false, erro: 'falha_decrypt' };
  }
}

/** Dispara o download no navegador. Separado da busca de propósito (ver acima). */
export function salvarComo(bytes: Uint8Array, nome: string): void {
  // `octet-stream`: o tipo real do arquivo não é conhecido (não é persistido, ver
  // cabeçalho). O SO decide pela extensão que a pessoa der ao salvar.
  const copia = new Uint8Array(bytes);
  const url = URL.createObjectURL(new Blob([copia], { type: 'application/octet-stream' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  a.click();
  // Revoga no próximo tick: revogar em seguida, na mesma execução, cancela o download
  // em alguns navegadores antes de ele começar.
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 0);
}

function base64ParaBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
